<template>
  <div data-view="users">
    <router-view v-slot="slotProps">
      <component :is="slotProps?.Component" @saved="onSaved" />
    </router-view>
    <layout-logged-frame :title="$t('users')" has-before>
      <template v-slot:header-actions>
        <Can action="store" subject="user">
          <v-btn
            color="primary"
            @click="$router.push({ name: 'users-new', query: $route.query })"
          >
            <v-icon>add</v-icon>
            {{ $t('add_new_user') }}
          </v-btn>
        </Can>
      </template>

      <template v-slot:before>
        <NavigationTabs
          :tabs="navigationTabs"
          :tabs-items-counter="usersCount"
        />
      </template>

      <template v-slot:content>
        <UsersFilters v-if="hasFilters" @filter="onFilter" @clearFilters="onClearFilters" />

        <table-base
          :columns="columns"
          :items="users"
          :pages="totalPages"
          :pagination="pagination"
          :is-loading="isLoading"
          has-custom-items-template
          :on-select-item="selectUser"
          :on-update-pagination="onUpdatePagination"
          :sync-to-url="true"
          @init-pagination="onInitPagination"
          :on-change-page="onChangePage"
        >
          <template v-slot:item-cell="scope">
            <template v-if="scope.cell.column.value === 'status.name'">
              <StatusPanel
                table-group="users"
                :status="scope.cell.item.status.name"
              />
            </template>

            <template v-else-if="scope.cell.column.value === 'role.name'">
              {{ displayRoleColumn(scope.cell.item) }}
            </template>

            <template v-else>
              {{ displayColumnValue(scope.cell.item, scope.cell.column.value) }}
            </template>
          </template>
        </table-base>
      </template>
    </layout-logged-frame>
  </div>
</template>

<script>
  import UsersFilters from '../../components/forms/filters/User';
  import urlPaginationRestore from '@/mixins/urlPaginationRestore';
  import StatusPanel from '../../components/panels/Status';
  import {extractNestedProp, isStr} from '../../utils';
  import columns from '../../config/users/columns';
  import navigationTabs from '../../config/users/navigationTabs';
  import NavigationTabs from '../../components/NavigationTabs';

  export default {
    mixins: [urlPaginationRestore],
    components: {
      UsersFilters,
      StatusPanel,
      NavigationTabs
    },
    data() {
      return {
        columns,
        pagination: {
          page: 1,
          rowsPerPage: 15,
          sortBy: 'created_at',
          order: 'desc' ,
          totalItems: 0,
        },
        usersCount: [],
        navigationTabs,
        totalPages: 0,
        query: {},
        isLoading: false,
      };
    },
    computed: {
      users() {
        return this.$store.getters['users/items'];
      },
      totalCount() {
        return this.pagination.totalItems || 0;
      },
      hasFilters() {
        return (
          this.$ability.can('see-user-status', 'filter') ||
          this.$ability.can('see-user-role', 'filter') ||
          this.$ability.can('see-user-search', 'filter') ||
          this.$ability.can('see-user-business', 'filter')
        );
      },
    },
    created() {
       this.getUsersCount();
    },
    methods: {
      // Emitted by the dialog routes after a successful save. Keeps the
      // refetch in this view, with this view's own query, and out of the URL.
      onSaved() {
        this.getUsers(this.query);
      },
      getUsersCount() {
        this.$store.dispatch('users/getUsersCount')
          .then(res => { this.usersCount = res })
          .catch(err => err);
      },
      getUsers(query = {}) {
        this.isLoading = true;
        this.pagination.order = query.order;

        return this.$store
          .dispatch('users/getItems', query)
          .then((res) => {
            const { itemsMeta } = this.$store.getters['users/itemsData'];
            const { total, current_page, last_page } = itemsMeta;

            this.pagination.page = current_page;
            this.pagination.totalItems = total;
            this.totalPages = last_page;
            this.isLoading = false;

            return res;
          })
          .catch((err) => {
            this.isLoading = false;
            return err;
          });
      },
      selectUser(user) {
        this.$store.dispatch('users/setActiveItem', user);
        this.$router.push({ name: 'users-edit', params: { id: user.id }, query: this.$route.query });
      },
      onUpdatePagination({ page, order, sortBy }) {
        const { query, pagination } = this;
        this.pagination.sortBy = isStr(sortBy) ? sortBy : pagination.sortBy;

        const nextQuery = {
          ...query,
          page,
          order,
          sort: isStr(sortBy) ? sortBy : pagination.sortBy,
        };

        this.query = nextQuery;

        this.runInitialFetch(() => this.getUsers(nextQuery));
      },
      onChangePage(page) {
        const { query, pagination } = this;
        const { order, sortBy } = pagination;
        const nextQuery = {
          ...query,
          page,
          order,
          sort: sortBy,
        };

        this.query = nextQuery;

        this.getUsers(nextQuery);
      },
      onFilter(filters) {
        this.query = {
          ...this.query,
          ...filters,
          page: 1,
        };

        this.runInitialFetch(() => this.getUsers(this.query));
      },
      onClearFilters() {
        const { sortBy, order } = this.pagination;
        this.query = { sort: sortBy, order };

        this.getUsers(this.query);
      },
      displayRoleColumn(item) {
        return item.role.name;
      },
      displayColumnValue(item, columnValue) {
        return extractNestedProp(item, columnValue);
      },
    },
  };
</script>

<style lang="scss">
@use '../../assets/styles/theme' as *;

.v-datatable {
  min-width: $table-min-width;
}
</style>
